import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import wordmark from "../../assets/brand/wordmark.svg";
import { NAV_SECTIONS } from "../../shared/config/site";
import { langFromPath, localizedPath } from "../../shared/i18n/lang";
import { openMerchantStore } from "../../shared/lib/storeLink";
import { clamp, useScrollLoop } from "../../shared/motion";
import { Btn, Icon, LangSwitch, SectionLink } from "../../shared/ui";
import { Dock } from "../dock/Dock";
import { MenuSheet } from "./MenuSheet";
import "./header.css";

// Tracks which section is currently in the viewport, to highlight the active nav item.
function useActiveSection(ids: readonly string[]) {
  const [active, setActive] = useState("");
  useEffect(() => {
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ids]);
  return active;
}

const NAV_IDS = NAV_SECTIONS.map((s) => s.id);

const Header = ({ minimal = false }: { minimal?: boolean }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  const hdrRef = useRef<HTMLElement>(null);
  const progRef = useRef<HTMLElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(NAV_IDS);

  // Scrolled shadow + progress bar are written to the DOM directly (no setState per frame).
  useScrollLoop((y) => {
    hdrRef.current?.classList.toggle("scrolled", y > 4);
    if (progRef.current) {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? clamp(y / max, 0, 1) : 0;
      progRef.current.style.transform = `scaleX(${p})`;
    }
  });

  return (
    <>
      <header className="hdr" id="hdr" ref={hdrRef}>
        <div className="wrap hdr-in">
          <Link to={localizedPath("/", lang)} className="logo" aria-label="Nasiyah">
            <img src={wordmark} alt="Nasiyah" />
          </Link>

          {minimal ? (
            <div className="hdr-r">
              <LangSwitch />
            </div>
          ) : (
            <>
              <nav className="nav">
                {NAV_SECTIONS.map((s) => (
                  <SectionLink key={s.id} id={s.id} current={active === s.id}>
                    {t(s.key)}
                  </SectionLink>
                ))}
              </nav>
              <div className="hdr-r">
                <LangSwitch />
                {/* Smart store deep-link; href is the no-JS fallback (D4). */}
                <Btn
                  as="a"
                  variant="primary"
                  size="sm"
                  href="#download"
                  onClick={(e) => {
                    e.preventDefault();
                    openMerchantStore();
                  }}
                >
                  {t("nav.cta")}
                </Btn>
                <button
                  type="button"
                  className="menu-btn"
                  id="menuBtn"
                  ref={menuBtnRef}
                  aria-label={t("menu.open")}
                  aria-expanded={open}
                  aria-controls="menu"
                  onClick={() => setOpen(true)}
                >
                  <Icon name="i-menu" />
                </button>
              </div>
            </>
          )}
        </div>
        {!minimal && <i className="prog" ref={progRef} />}
      </header>
      {!minimal && (
        <>
          <MenuSheet open={open} onClose={() => setOpen(false)} returnFocusRef={menuBtnRef} />
          <Dock />
        </>
      )}
    </>
  );
};

export default Header;
